import { KnownSteps, TrimmedSession } from "./models/session.model";
import { computed, Injectable, signal} from '@angular/core';
import { firstValueFrom, map, of } from 'rxjs';
import { ItemFormat } from "./models/itemFormat.model";
import { Plan } from "./models/plan.model";
import { CustomerFormat } from "./models/customerFormat.model";
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { StepService } from "./step.service";
import { PromoService } from "./promo.service";

import { StepState, Tips, PageNotes } from "./models/step.model";
import { catchError, retry } from 'rxjs/operators';
import { DirectApiService, UserLookup } from './directApi.service';
import { AgilityContentService } from "./agility-content-model.service";
import { AgilityContentModel } from "./models/agility-content.model";
import { environment } from "../../environments/environment";


@Injectable({
    providedIn: 'root'
})

export class DirectSessionLogicService {

    private factsForFooter: Tips= {} as Tips;

//-------v CUSTOMER
    private _customerSignal = signal<CustomerFormat>({} as CustomerFormat);
    public customerSignal = computed(() => this._customerSignal())
    get customerSignalValue(): CustomerFormat {
        return this._customerSignal();
    }
    updateCustomerSignal(customer: CustomerFormat) {
        this._customerSignal.set(customer);
    }
//-------^ CUSTOMER


//-------v TRIMMED_SESSION
    private _trimmedSessionSignal = signal<TrimmedSession>({} as TrimmedSession);
    public trimmedSessionSignal = computed(() => this._trimmedSessionSignal());
    get trimmedSessionSignalValue(): TrimmedSession {
        return this._trimmedSessionSignal();
    }
    updateTrimmedSessionSignal(session: TrimmedSession) {
        this._trimmedSessionSignal.set(session);
    }
    updateCustomerReferenceNumber( id: string ) {
        this._trimmedSessionSignal.set({
            ...this._trimmedSessionSignal(),
            customerId: id
        });
    }
    updateTotalPriceInSessionSignal( price: number ) {
        this._trimmedSessionSignal.set({
            ...this._trimmedSessionSignal(),
            totalCost: price
        });
    }
//-------^ TRIMMED_SESSION


//-------v CURRENT_STEP
    //current step signal??
    private _currentStepSignal = signal<number>(0);
    public currentStepSignal = computed(() => this._currentStepSignal());
    completeCurrentStepSignal() {
        let current = this._currentStepSignal();

        let currentKSteps = this.knownStepsListSignalValue;
        currentKSteps[current].completed = true;

        this._knownStepsListSignal.set(currentKSteps); //update known steps signal
        //this._currentStepSignal.set(current + 1); //incriment current step
    }
    unCompleteCurrentStepSignal() {
        let current = this._currentStepSignal();
        let currentKSteps = this.knownStepsListSignalValue;
        currentKSteps[current].completed = false;

        this._knownStepsListSignal.set(currentKSteps); //update known steps signal
    }
    unCompleteStepAfter(page: number) {
        let currentKSteps = this.knownStepsListSignalValue;
        currentKSteps.forEach((step, index) => {
            if(index > page){
                step.completed = false;
            }
        });
        this._knownStepsListSignal.set(currentKSteps); //update known steps signal
    }
    verifyCurrentStepLockout(){
        const currentStep = this.knownStepsListSignalValue[this._currentStepSignal()];

        if(currentStep.requiredChoice === false){
            this.completeCurrentStepSignal();
        }else{
            let itemsInStep = this.cartItemsSignalValue?.filter(item =>
                item.itemPageRef === currentStep.reference);

            if(itemsInStep && itemsInStep.length > 0) this.completeCurrentStepSignal();
        }   
    }
    changingStepSignal(step: number) {
        const targetStep = this.getPageReference(step);
        const currentStep = this.getPageReference(step-1);

        let currentStepComplete = currentStep?.completed || currentStep?.requiredChoice == false;
        let targetStepReady = (targetStep && targetStep.completed) || (currentStep && currentStep.completed);
        
        if(targetStepReady || currentStepComplete){
            if (targetStepReady) {
                this._currentStepSignal.set(step);
                return true;
            }else{
                this.directApiService.setLoading("%&%Cannot change step to: " + targetStep?.breadcrumbName);
                console.log("cannot change step to: ", step);
            }
        }else{
            this.directApiService.setLoading("%&%Please make a selection before continuing");
            console.log("cannot change step to: ", step);
        }
        return false;
    }
    getPageReference(page: number) {
        return this.knownStepsListSignalValue[page];
    }
    getPageReferenceSignal(page: number) {
        return this.stepsListSignalValue[page].urlRef;
    }
//-------^ CURRENT_STEP

//-------v PLAN
    private _planSelectedSignal = signal<Plan>({} as Plan);
    public planSelectedSignal = computed(() => this._planSelectedSignal());
    get selectedPlanSignalValue(): Plan {
        return this._planSelectedSignal();
    }
    async setPlanSelectedSignal(plan: Plan) {
        this._planSelectedSignal.set(plan);
        this.recalculateTotalCostInSessionSignal();

        let testStepsList = this.stepService.stepsViewSignalValue;
        if(testStepsList.length != 0){
            try{
                let stepsToUse = testStepsList.filter(step => 
                    !this.selectedPlanSignalValue.stepExclusions?.includes(step.urlRef)
                );     
                
                //set steps list signal
                this._stepsListSignal.set(stepsToUse);

            }catch{
                //error catching for no plan or no steps
                this.escapeToTopLevelSignal();
            }
        }else{
            console.log("no plan, no steps");
            //
        }
    }

    setCustomerPlan(plan: Plan) {
        this.setPlanSelectedSignal(plan);
        this.router.navigate(['/signup']);
    }
//--------^ PLAN

//-------v KNOWN_STEPS
    private _knownStepsListSignal = signal<KnownSteps[]>([]);
    public knownStepsListSignal = computed(() => this._knownStepsListSignal());
    get knownStepsListSignalValue(): KnownSteps[] {
        if(this._knownStepsListSignal().length === 0){
            this.generateKnownStepsListSignal();
        }
        return this._knownStepsListSignal();
    }
    generateKnownStepsListSignal() {
        if(this.stepsListSignalValue.length != 0){
            let mappedKnownSteps: KnownSteps[] = [];

            this.stepsListSignalValue.forEach((step,index) => {
                mappedKnownSteps.push(
                    {
                        index: index , 
                        completed: false, 
                        reference: step.urlRef,
                        breadcrumbName: step.name,
                        requiredChoice: step.requiredChoice
                    }
                )
            });
            
            this._knownStepsListSignal.set(mappedKnownSteps);
        }
    }
    getCurrentStepSignal(){
        if(this._currentStepSignal() === undefined){
            this._currentStepSignal.set(0);
        }
        return this.knownStepsListSignal()[this._currentStepSignal()];
    }
    getStepAtIndexSignal(index: number) {
        return this.knownStepsListSignal()[index];
    }
    completeCurrentPage() {
        let current = this._currentStepSignal();

        let currentKSteps = this.knownStepsListSignalValue;
        currentKSteps[current].completed = true;
        this._knownStepsListSignal.set(currentKSteps); //update known steps signal
    }
    unCompleteCurrentPage() {
        let current = this._currentStepSignal();
        let currentKSteps = this.knownStepsListSignalValue;
        currentKSteps[current].completed = false;
        this._knownStepsListSignal.set(currentKSteps); //update known steps signal
    }
    replaceKnownSteps(newSteps : KnownSteps[]){
        this._knownStepsListSignal.set(newSteps);
    }
//-------^ KNOWN_STEPS


//-------v BASE_STEPS
    BaseStepsLockout: boolean = false;
    private _stepsListSignal = signal<StepState[]>([]);
    public stepsListSignal = computed(() => this._stepsListSignal());
    get stepsListSignalValue(): StepState[] {
        if(this._stepsListSignal().length === 0 && !this.BaseStepsLockout ){
            this.initStepsListSignal().then(() => {
                return this._stepsListSignal();
                this.BaseStepsLockout = true;
            });
        }
        return this._stepsListSignal();
    }
    async initStepsListSignal() {
        try{
            if(this._stepsListSignal().length === 0){
                const response = await firstValueFrom( await this.directApiService.fetchSteps() );
                this._stepsListSignal.set(response as StepState[]);
            }

        }catch(e){
            console.error("Error fetching steps: ", e);
            this._stepsListSignal.set([]);
        }
    }
    SetBaseStepsListSignal(steps: StepState[]) {
        this._stepsListSignal.set(steps);
    }

    escapeToTopLevelSignal() {
        this._currentStepSignal.set(0);
        this.unCompleteStepAfter(-1);

        if(environment.ConnectionInfo.localDebug === "true")
        { 
            this.router.navigate(['/plans']); 
        }else{ 
            this.router.navigate(['/']); 
        }
    }
    redirectToStepSignal(stepRef: string) {
        console.log("redirecting to: ", stepRef);
        let stepIndex = 0;

        let currentKSteps = this.knownStepsListSignalValue;
        currentKSteps.forEach((step, index) => {
            if(step.reference === stepRef){
                this._currentStepSignal.set(index);
                stepIndex = index;
            }
        });

        let updatedSteps = currentKSteps.map((step, index) => {
            if(index < stepIndex){
                step.completed = true;
            }
            if(index > stepIndex){
                step.completed = false;
            }
            return step;
        });
        this._knownStepsListSignal.set(updatedSteps);
        this.router.navigate([stepRef]);
    }
//-------^ BASE_STEPS





//-------v ITEMS
    private _cartItemsSignal = signal<ItemFormat[]>([]);
    public cartItemsSignal = computed(() => this._cartItemsSignal());
    get cartItemsSignalValue(): ItemFormat[] {
        return this._cartItemsSignal();
    }
    addItemToCartItemsSignal(item: ItemFormat) {
        const currentItems = this._cartItemsSignal();
        this._cartItemsSignal.set([...currentItems, item]);
        //run recalculate total cost
        this.recalculateTotalCostInSessionSignal();
    }
    removeItemFromCartItemsSignal(item: ItemFormat) {
        const currentItems = this._cartItemsSignal();
        this._cartItemsSignal.set(currentItems.filter(i => i !== item));
        //run recalculate total cost
        this.recalculateTotalCostInSessionSignal();
    }
//-------^ ITEMS



//-------v AGILITY
    AgilityChecked: boolean = false;
    private _agilityObjectsSignal = signal<AgilityContentModel >({} as AgilityContentModel);
    public agilityObjectsSignal = computed(() => this._agilityObjectsSignal())
    get agilityObjectsSignalValue(): AgilityContentModel  {
        if(this.agilityObjectsSignal().bodyAssociatedBrandingLogo === undefined && !this.AgilityChecked){
            this.initAgilityObjectsSignal();
            this.AgilityChecked = true;
        }
        return this.agilityObjectsSignal();
    }
    async initAgilityObjectsSignal() {
        //need to get from agility service and set the signal
        const contentModelResp = await firstValueFrom( await this.agilityService.getContentModel() );

        let contentModel = contentModelResp as AgilityContentModel;
        // contentModel.bodyAssociatedBrandingLogo.url = this.contentModel.bodyAssociatedBrandingLogo.url + "?format=auto";
        // contentModel.companyFooterLogo.url = this.contentModel.companyFooterLogo.url + "?format=auto";

    
        this._agilityObjectsSignal.set(contentModel);
    }
//-------^ AGILITY



    constructor(
        private http: HttpClient,
        private stepService: StepService,
        public router: Router,
        public promoService: PromoService,
        public directApiService: DirectApiService,
        public agilityService: AgilityContentService
    ) {

    }


    async setSessionIp() {
        try {
            const ip = await this.http.get<{ ip: string }>('https://api.ipify.org?format=json')
                .pipe(
                retry(3), // Retry up to 3 times before failing
                map(response => response.ip || 'null'),
                catchError(error => {
                    console.error("error fetching IP: ", error);
                    return of('null');
                })
                )
                .toPromise();
            let session = this.trimmedSessionSignalValue;
            session.ipAddress = ip ?? "";
            this.updateTrimmedSessionSignal(session);
        } catch (error) {
            console.error("Failed to set session IP: ", error);
        }
    }


    

    async getCustomerFromForte(appNumber: string, companyID: number) {
        let myCompanyID = environment.CompanyInfo.CompanyId;
        this.directApiService.getContactInfo( {appNumber, companyID} as UserLookup ).subscribe( (resp: any) => {
            if(
                resp != null 
                && (resp.response.getLeadDataResponseItem.responseCode == '0000' 
                || resp.response.getLeadDataResponseItem.responseCode == '0009')
            )
            {
                const quoteDetail = resp.response.getLeadDataResponseItem.quoteDetail;

                const customer = {
                    firstName:      quoteDetail.firstName ?? "",
                    lastName:       quoteDetail.lastName?? "",
                    email:          quoteDetail.email?? "",
                    phoneNumber:    quoteDetail.phone?? "",
                    address:        quoteDetail.address?? "",
                    city:           quoteDetail.city?? "",
                    state:          quoteDetail.state?? "",
                    zip:            quoteDetail.zip?? "",
                    country:        "United States",    //-AG as far as I know we're only selling in the US
                } as CustomerFormat;

                this.updateCustomerSignal(customer);
                
                if(companyID == myCompanyID){
                    //need to update the trimmed session object here
                    this.updateCustomerReferenceNumber(appNumber);
                }
      
            }
            else console.log("customer not in forte: ", appNumber, " ", companyID);
        });
    }
    

    async getFactsForGivenStep(step:string): Promise<Tips> {
        //Get all facts for the given step with match on url
        let thisStep = await this.stepService.getStepFromUrl(step) || {} as StepState;


        //selecting random or default tip from the given step
        if(thisStep.pageTips){
            this.factsForFooter = thisStep
                .pageTips[Math.floor(Math.random() * thisStep.pageTips.length)]         
        }else{
            this.factsForFooter = {
                tipString: "No tips available for this step",
                tipIconValue: "error",
                tipSource: "",
                tipSourceUrl: "https://www.google.com"
            };
        }

        return this.factsForFooter;
    }

    async getTermDetailsForGivenStep(step: string): Promise<PageNotes[]> {
        //Get all term details for the given step with match on url
        let thisStep = await this.stepService.getStepFromUrl(step) || {} as StepState;
        let termDetails: PageNotes[] = [];

        //selecting random or default tip from the given step
        if(thisStep.pageNotes){
            termDetails = thisStep.pageNotes;
        }else{
            termDetails = [];
        }

        return termDetails;
    }


    public recalculateTotalCostInSessionSignal() {
        let totalCost = this.trimmedSessionSignalValue.totalCost ?? 0;
        //add all items to the plan price
        totalCost = this.planSelectedSignal().price || 0.00;
        if(this.cartItemsSignal()?.length > 0) {
            this.cartItemsSignal()?.forEach(item => {
                totalCost += item.price;
            });
        }
 
        let coupon = this.promoService.couponSignal();
        if(coupon.code != undefined){
            if(coupon.code != null || coupon.code != undefined){
                if(coupon?.dollarDiscount > 0){
                    totalCost -= coupon.dollarDiscount;
                }
                if(coupon?.percentDiscount > 0){
                    totalCost -= totalCost * (coupon.percentDiscount/100);
                }
            }
        }
        if(totalCost > 0){
            totalCost = parseFloat( totalCost.toFixed(2) );
            this.updateTotalPriceInSessionSignal(totalCost);
        }
    }

    returnDiscount(): number {
        let protoTotal = 0;

        let selections = this._cartItemsSignal();
        let plan = this.planSelectedSignal();
        let coupon = this.promoService.couponSignal();

        if(plan?.name?.length > 0){
            protoTotal += plan.price;
        } 

        if(selections.length > 0) {
            selections?.forEach(item => {
                protoTotal += item.price;
            });
        }

        let discountAmmount = 0
        if(coupon){
            discountAmmount += coupon.dollarDiscount + (protoTotal * coupon.percentDiscount/100);
        }
        
        return discountAmmount;
    }

    
    redirectToStep(stepRef: string) {
        console.log("redirecting to: ", stepRef);

        let newCurrentStep = -1;
        let currentKnownSteps = this.knownStepsListSignal();

        currentKnownSteps.forEach((step, index) => {
            if(step.reference === stepRef){
                newCurrentStep = index;
            }
        });

        if(newCurrentStep > 0){
            let updatedSteps = currentKnownSteps.map((step, index) => {
                if(index < newCurrentStep){
                    step.completed = true;
                }
                if(index > newCurrentStep){
                    step.completed = false;
                }
                return step;
            });
    
            this.replaceKnownSteps(currentKnownSteps);
    
            this.router.navigate([stepRef]);
        }else{
            console.error("could not find step to redirect to: ", stepRef);
        }

    }









    sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
